'use client';
import { motion } from 'framer-motion';
import MinimalVideoPlayer from './minimal-video-player';

interface VideoShowcaseProps {
  title: string;
  description: string;
  additionalText?: string;
  videoSrc: string;
  thumbnailSrc: string;
  videoTitle: string;
  isRight?: boolean;
}

export default function VideoShowcase({
  title,
  description,
  additionalText,
  videoSrc,
  thumbnailSrc,
  videoTitle,
  isRight = false,
}: VideoShowcaseProps) {
  return (
    <section className="py-8 md:py-32 bg-white dark:bg-black relative overflow-hidden">
      {/* Background gradient */}
      <div
        className={`absolute ${
          isRight ? 'left-0' : 'right-0'
        } top-1/2 -translate-y-1/2 size-[500px] rounded-full opacity-10 blur-[120px] pointer-events-none`}
        style={{
          background: isRight
            ? 'radial-gradient(circle at center, rgba(77, 46, 245, 0.4), rgba(125, 81, 237, 0.1), transparent)'
            : 'radial-gradient(circle at center, rgba(77, 46, 245, 0.4), rgba(125, 81, 237, 0.1), transparent)',
        }}
      />

      <div className="container mx-auto px-4">
        <div className="max-w-7xl mx-auto">
          <div
            className={`grid md:grid-cols-2 gap-8 md:gap-16 lg:gap-24 items-center ${
              isRight ? 'md:flex-row-reverse' : ''
            }`}
          >
            <motion.div
              className="space-y-8"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div>
                <h2 className="text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold tracking-tight text-zinc-900 dark:text-white mb-4 md:mb-6 leading-tight">
                  {title}
                </h2>
                <p className="text-base md:text-lg lg:text-xl text-zinc-600 dark:text-zinc-400 leading-relaxed">
                  {description}
                </p>
                {additionalText && (
                  <p className="text-base md:text-lg lg:text-xl text-zinc-600 dark:text-zinc-400 leading-relaxed mt-4">
                    {additionalText}
                  </p>
                )}
              </div>
            </motion.div>

            <motion.div
              className={`order-first ${
                isRight ? 'md:order-last' : 'md:order-first'
              }`}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <MinimalVideoPlayer
                videoSrc={videoSrc}
                thumbnailSrc={thumbnailSrc}
                title={videoTitle}
                className="shadow-lg md:shadow-xl rounded-lg md:rounded-xl"
                autoplay={true}
              />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
